import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";
import {consume} from "@lit/context";
import {LitElement, html, nothing, unsafeCSS} from "lit";
import {customElement, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";

import type {BridgeBarConfig} from "../../config";
import {BridgeBarConfigContext} from "../../config";
import {
  COLLAPSE_COMPONENTS_EVENT,
  ORG_SELECTOR_PRESENCE_EVENT,
  ROOT_TAG
} from "../../globals";
import {KeepAliveService} from "../../keep-alive";
import type {Environment} from "../../runtime";
import {RuntimeContext} from "../../runtime";
import {sanitizeHtml} from "../../security";
import styles from "./BBContainerElement.css?inline";
import {unsafeStatic} from "lit/static-html.js";
import {BridgeBarDataContext} from "../../backend";
import type {BridgeBarDataContextValue} from "../../backend";
import {buildBbShownPayload, emit} from "../../analytics";

const tagName = `${ROOT_TAG}-container`;
export const BridgeBar = unsafeStatic(tagName);
/**
 * Orchestrates the Bridge Bar layout via named slots and instantiates
 * the session keep-alive service.
 *
 * Slot order: side-nav | logo | switcher | context-selector, then a right-hand
 * cluster pushed over by `ml-auto` holding support-menu | dealer-label |
 * notifications | avatar. The context-selector is centered over the bar from
 * 760px up, so above that width its slot is out of flow and its DOM position
 * between switcher and the cluster no longer reflects where it appears.
 *
 * The container consumes BridgeBarConfigContext for dealerLabelHtml and
 * contextType (which between them decide whether the middle of the bar holds a
 * static dealer label or the org context selector, and therefore how much room
 * the logo has), and RuntimeContext for the keep-alive endpoint and partition. No
 * child components are imported — consumers project them into the slots so a
 * broken child import does not prevent the container from rendering.
 */
@customElement(tagName)
export class BBContainerElement extends LitElement {
  static styles = unsafeCSS(styles);

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  @consume({context: RuntimeContext, subscribe: true})
  private runtime: RuntimeEnvironment<Environment, unknown> | undefined;

  @consume({context: BridgeBarDataContext, subscribe: true})
  private bridgeBarData: BridgeBarDataContextValue | undefined;

  /** Lazily instantiated once both contexts are available. */
  private keepAliveService: KeepAliveService | null = null;

  /** Guards the fire-once bbShown emission (AD-5, F-8). */
  private bbShownFired = false;

  /** Names of currently-open child components (e.g. "user-profile-menu",
   *  "application-switcher"). The overlay renders while this set is non-empty.
   *  A set (not a boolean) is required because a single click can both open
   *  one menu and close another, dispatching expand+collapse in either order. */
  @state()
  private openComponents = new Set<string>();

  /**
   * False once the org context selector has told us it has nothing to show —
   * membership resolved with zero entities, so no selector will ever appear for
   * this user. Starts true so the layout reserves room from the first paint and
   * the logo does not appear and then vanish on the common path.
   */
  @state()
  private orgSelectorPresent = true;

  private handleOrgSelectorPresence = (e: Event) => {
    const present = (e as CustomEvent<{present?: boolean}>).detail?.present;
    if (typeof present !== "boolean") return;
    this.orgSelectorPresent = present;
  };

  private handleComponentExpanded = (e: Event) => {
    const name = (e as CustomEvent).detail?.component;
    if (typeof name !== "string") return;
    const next = new Set(this.openComponents);
    next.add(name);
    this.openComponents = next;
  };

  private handleComponentCollapsed = (e: Event) => {
    const name = (e as CustomEvent).detail?.component;
    if (typeof name !== "string") return;
    if (!this.openComponents.has(name)) return;
    const next = new Set(this.openComponents);
    next.delete(name);
    this.openComponents = next;
  };

  /**
   * Collapse every open Bridge Bar child component (application switcher,
   * user profile menu, etc.).
   *
   * Children own their open state, so the container cannot close them
   * directly. Instead it broadcasts {@link COLLAPSE_COMPONENTS_EVENT} on
   * `document`; each child listens for it and closes itself, re-emitting
   * `component-collapsed` through the normal flow. The container also clears
   * its own `openComponents` set so the overlay is removed immediately and
   * deterministically, even if a child is already closed or never re-emits.
   */
  collapseComponents(): void {
    document.dispatchEvent(new CustomEvent(COLLAPSE_COMPONENTS_EVENT));
    if (this.openComponents.size > 0) {
      this.openComponents = new Set();
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.initKeepAlive();
    this.addEventListener("component-expanded", this.handleComponentExpanded);
    this.addEventListener("component-collapsed", this.handleComponentCollapsed);
    this.addEventListener(
      ORG_SELECTOR_PRESENCE_EVENT,
      this.handleOrgSelectorPresence
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.keepAliveService?.destroy();
    this.keepAliveService = null;
    this.removeEventListener(
      "component-expanded",
      this.handleComponentExpanded
    );
    this.removeEventListener(
      "component-collapsed",
      this.handleComponentCollapsed
    );
    this.removeEventListener(
      ORG_SELECTOR_PRESENCE_EVENT,
      this.handleOrgSelectorPresence
    );
  }

  override updated(): void {
    // Reflect theme onto the host so :host([data-ids-theme-mode="dark"])
    // selectors match. :host-context() is not supported in Firefox, so
    // each themed component must carry the attribute on its own host.
    this.setAttribute("data-ids-theme-mode", this.config?.theme ?? "light");
    // RuntimeContext resolves asynchronously; re-attempt on each update
    // until the service has been created.
    this.initKeepAlive();
    // Emit bbShown once when the data context first leaves pending (AD-5, F-7, F-8).
    // Fires on both complete and error — mirrors legacy error-parity behaviour.
    this.initBbShown();
  }

  /**
   * Emits the `bbShown` GA event once the data context first leaves `pending`.
   * Idempotent — returns early if already fired or data/config unavailable.
   * Follows the same guarded side-effect pattern as {@link initKeepAlive}.
   */
  private initBbShown(): void {
    if (this.bbShownFired) {
      return;
    }
    const data = this.bridgeBarData;
    if (!data || data.status === "pending") {
      return;
    }
    if (!this.config) {
      return;
    }
    const error = data.status === "error" ? data.error : undefined;
    const bridgeUser =
      data.status === "complete"
        ? data.data
        : {
            id: null as unknown as string,
            username: "",
            given_name: "",
            family_name: "",
            solution_links: [],
            display_info: [],
            user_links: [],
            feature_flags: {}
          };
    const payload = buildBbShownPayload(
      bridgeUser,
      this.config,
      data.status,
      error
    );
    emit({event: "bbShown", ...payload}, this.config.getTracker);
    this.bbShownFired = true;
  }

  /**
   * Creates the KeepAliveService once the runtime context becomes available.
   * Idempotent — returns early if already instantiated or inputs missing.
   */
  private initKeepAlive(): void {
    if (this.keepAliveService) {
      return;
    }
    const environment = this.runtime?.environment;
    const partition = this.runtime?.runtime?.partition;
    if (!environment?.bridgeBarApiUrl || !partition) {
      return;
    }
    const keepAliveUrl = `${environment.bridgeBarApiUrl}/keep-alive`;
    this.keepAliveService = new KeepAliveService(keepAliveUrl, partition);
  }

  /**
   * Whether the middle of the bar needs to leave room for the org context
   * selector, which is what decides whether the logo yields below 520px.
   *
   * Mostly the adopter's declared config: normalizeBridgeBarConfig treats
   * `contextType` and `dealerLabelHtml` as mutually exclusive and drops the
   * latter when both are given, so at most one of the two ever wants that space.
   *
   * The one runtime input is the selector announcing whether it puts anything in
   * the slot. A user with zero memberships still does — they get the
   * no-dealerships message — so they yield the logo like everyone else; giving
   * them the logo instead would make them the only group that never learns why
   * their bar has no dealership on a narrow viewport. The signal turns negative
   * only when the selector can render nothing at all, i.e. no user identity.
   *
   * Deliberately a settled signal: assuming a selector is coming until told
   * otherwise is what keeps the logo from flashing in and out while membership
   * loads.
   */
  private get showsOrgSelector(): boolean {
    return (
      this.config?.contextType === "common-org-entity" &&
      this.orgSelectorPresent
    );
  }

  private dealerLabelTemplate() {
    // No suppression needed: the selector and this label are mutually exclusive
    // in config, so `dealerLabelHtml` is already absent whenever the selector
    // owns the middle of the bar.
    const raw = this.config?.dealerLabelHtml;
    if (!raw) {
      return nothing;
    }
    const safe = sanitizeHtml(raw);
    return html`<span
      data-testid="dealer-label"
      class="ids-type font-bridge tracking-[0.6px] leading-none truncate text-right min-w-0 text-primary-700 dark:text-white hidden md:block"
      data-ids-size="md"
      >${unsafeHTML(safe)}</span
    >`;
  }

  /** Matches a valid CSS pixel length, e.g. "1200px", "0.5px", ".5px". */
  private static readonly PX_WIDTH = /^\d*\.?\d+px$/i;

  /**
   * Horizontal padding that centers the bar's content over the configured
   * page width. Returns an empty string when no *valid* width is configured,
   * in which case `px-4` is applied via the class list instead. See original
   * bridge bar width usage.
   *
   * Callers build `width` by appending "px" to a number, so a missing or
   * blank number arrives as just "px" (or ""). Those are truthy-but-invalid:
   * they would emit `calc(49.9% - (px) / 2)`, which the browser drops, AND
   * suppress the `px-4` fallback — removing the padding entirely (visual
   * regression). So only emit the calc when `width` is a real `<number>px`.
   */
  private horizontalPaddingStyle(): string {
    const width = this.config?.width?.trim();
    if (!width || !BBContainerElement.PX_WIDTH.test(width)) {
      return "";
    }
    return `padding-inline: calc(49.9% - (${width}) / 2)`;
  }

  override render() {
    const overlayVisible = this.openComponents.size > 0;
    const paddingStyle = this.horizontalPaddingStyle();
    return html`
      <div
        class="fixed top-0 left-0 w-screen h-[35px] bg-app dark:bg-primary-900 text-black dark:text-white shadow-sm flex flex-row items-center z-[99999] ${paddingStyle
          ? ""
          : "px-4"}"
        style=${paddingStyle}>
        <slot name="side-nav"></slot>
        <!-- With the selector active the logo appears as soon as the in-flow row
             has room for it, which is 504.5px: 16px padding + 164.5px logo +
             16px switcher + 8px gap + 196px selector + 8px gap + 80px for the
             bell and avatar + 16px padding. Nothing in this row shrinks (the
             slotted hosts sit behind shadow boundaries and the trigger button's
             auto width is fit-content), so the threshold has to clear that sum
             rather than rely on flex-shrink; 520px gives it 15px of slack. -->
        <div
          class=${classMap({
            "flex-shrink-0": true,
            "hidden": this.showsOrgSelector,
            "min-[520px]:block": this.showsOrgSelector
          })}>
          <slot name="logo"></slot>
        </div>
        <!-- The switcher leads the row so that on mobile its popover opens from
             the left edge of the bar and cannot run off the right side of the
             viewport. Wide layouts are unaffected by this DOM position: the
             selector below is absolute-centered from 760px, so the in-flow order
             there stays logo -> switcher -> right-hand cluster as before. -->
        <div class="flex-shrink-0"><slot name="switcher"></slot></div>
        <!-- Selector sits inline to the right of the switcher (ml-2 keeps the
             label off the switcher's grid icon) and is only absolute-centered
             from lg (960px) up.

             In flow it cannot overlap anything and absorbs the row's pressure by
             truncating (see the shrink rules in BBContainerElement.css). Once
             centered it is out of flow, so neither of those holds: it reaches
             (width / 2) +/- 168px with its label at its 320px cap regardless of
             what else is in the row, and flex-shrink cannot reach it.

             lg is the narrowest breakpoint where centering clears the left-hand
             chrome in *every* configuration. The binding case is the side-nav's
             float variant, which is in flow and independently flag-gated: with
             it enabled the logo and switcher extend to 287px, so centering needs
             2 * (287 + 168) = 910px of room. At the previous 760px threshold the
             centered selector covered the switcher outright and overlapped the
             logo by 59px. The right-hand cluster is never the binding side —
             even with the support menu it clears from ~642px. -->
        <div
          class="flex items-center min-w-0 ml-2 mr-auto lg:ml-0 lg:mr-0 lg:absolute lg:left-1/2 lg:top-0 lg:h-full lg:-translate-x-1/2"
          data-testid="context-selector-slot">
          <slot name="context-selector"></slot>
        </div>
        <div class="ml-auto flex flex-row items-center flex-shrink-0">
          <slot name="support-menu"></slot>
          ${this.dealerLabelTemplate()}
          <slot name="notifications"></slot>
          <slot name="avatar"></slot>
        </div>
      </div>
      ${overlayVisible
        ? html`<div
            data-testid="bridge-bar-menu-overlay"
            class="bridge-bar-menu-overlay fixed inset-0 z-[9999] bg-transparent"
            aria-hidden="true"></div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: BBContainerElement;
  }
}
